import * as React from 'react';
import { useRef } from 'react';
import { useLatestRef } from '@epihub/web/src/hooks/useLatestRef';
import { stopPropagation } from '@epihub/web/src/hooks/useStopPropagation';
import { mobileAndTabletCheck } from '../../lib/mobileCheck';

export function useTextInputFocusHandler() {
  const inputRef = React.createRef<HTMLInputElement>();
  const inputIsFocusedRef = useRef<boolean>(null);
  React.useEffect(() => {
    function checkInputIsFocused() {
      inputIsFocusedRef.current = document.activeElement === inputRef.current;
    }

    checkInputIsFocused();
    const interval = setInterval(() => checkInputIsFocused(), 500);
    return () => clearInterval(interval);
  }, [inputRef]);

  const refocusInput = React.useCallback(() => {
    if (inputIsFocusedRef.current) {
      inputRef.current?.focus();
    }
  }, [inputRef, inputIsFocusedRef]);

  const refocusHoc = React.useCallback(<T extends (...any: any) => any>(callback: T) => {
    const enhancedCallback = function(event: MouseEvent, ...others: any) {
      if (inputIsFocusedRef.current) {
        inputRef.current?.focus();
        stopPropagation(event);
      }
      return callback(event, ...others);
    };

    return {
      ...(mobileAndTabletCheck() ? { onMouseDown: enhancedCallback as T } : { onClick: enhancedCallback as T }),
    };
  }, [inputRef]);

  const value = {
    inputRef,
    refocusInput,
    refocusHoc,
  };

  const textInputFocusHandlerRef = useLatestRef(value);
  return {
    textInputFocusHandlerRef,
    ...value,
  };
}
